import { Link } from "react-router-dom";
import { Mail, MapPin, Heart, Instagram } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const categories = [
    { name: "Sabbatår intro", slug: "sabbataar-intro" },
    { name: "Idéer & inspiration", slug: "ideer-inspiration" },
    { name: "Rejse & udland", slug: "rejse-udland" },
    { name: "Højskole", slug: "hoejskole-uddannelse" },
  ];

  const helpLinks = [
    { name: "Om os", path: "/om-os" },
    { name: "FAQ", path: "/faq" },
    { name: "Kontakt", path: "/kontakt" },
  ];

  const legalLinks = [
    { name: "Privatlivspolitik", path: "/privacy" },
    { name: "Vilkår og betingelser", path: "/terms" },
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-4">
              <img 
                src="https://izkyvqbleuatzitwmtjw.supabase.co/storage/v1/object/public/pics2/Black%20Camera%20Icon%20Photography%20Logo%20(3).png" 
                alt="MySabbat" 
                className="h-12 w-auto" 
              />
            </Link>
            <p className="text-primary-foreground/80 text-sm leading-relaxed mb-4">
              Din guide til det perfekte sabbatår. Find inspiration, guides og værktøjer til at planlægge dit eventyr.
            </p>
            <div className="flex items-center gap-2 text-primary-foreground/90 text-sm">
              <MapPin className="w-4 h-4" />
              <span>Danmark</span>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Kategorier</h3>
            <ul className="space-y-3">
              {categories.map((category) => (
                <li key={category.slug}>
                  <Link 
                    to={`/kategori/${category.slug}`}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help & Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Hjælp & Info</h3>
            <ul className="space-y-3">
              {helpLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Juridisk</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="mt-6 pt-4 border-t border-primary-foreground/20">
              <h4 className="font-medium text-sm mb-3">Følg os</h4>
              <div className="flex items-center gap-3">
                <a 
                  href="https://instagram.com/mysabbat.dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors group"
                  aria-label="Følg MySabbat på Instagram"
                >
                  <Instagram className="w-4 h-4 text-primary-foreground group-hover:text-accent transition-colors" />
                </a>
                <a 
                  href="https://tiktok.com/@mysabbat.dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors group"
                  aria-label="Følg MySabbat på TikTok"
                >
                  <svg className="w-4 h-4 text-primary-foreground group-hover:text-accent transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://facebook.com/mysabbat.dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors group"
                  aria-label="Følg MySabbat på Facebook"
                >
                  <svg className="w-4 h-4 text-primary-foreground group-hover:text-accent transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Contact */}
            <div className="mt-4">
              <a 
                href="mailto:kontakt@mysabbat.dk"
                className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                kontakt@mysabbat.dk
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-primary-foreground/20">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/90">
            <p>© {currentYear} MySabbat. Alle rettigheder forbeholdes.</p>
            <p className="flex items-center gap-1">
              Lavet med <Heart className="w-4 h-4 text-accent fill-accent" /> i Danmark
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
